import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Section } from '@/components/Section';
import { getTrialConfirmation, type TrialConfirmation } from '@/lib/api';
import { events, track } from '@/lib/analytics';

const CLASS_LABEL: Record<string, string> = {
  BOXING: 'Boxing',
  MUAY_THAI: 'Muay Thai',
  MMA: 'MMA',
  BJJ: 'Brazilian Jiu-Jitsu',
  KIDS_BOXING: 'Kids Boxing',
  KIDS_BJJ: 'Kids Jiu Jitsu',
  KIDS_WRESTLING: 'Kids Wrestling',
};

/** Trial class choice → membership program token the schedule page filters on. */
const CLASS_TO_PROGRAM: Record<string, string> = {
  BOXING: 'Boxing',
  MUAY_THAI: 'MuayThai',
  MMA: 'MMA',
  BJJ: 'BJJ',
  KIDS_BOXING: 'Kids',
  KIDS_BJJ: 'Kids',
  KIDS_WRESTLING: 'Kids',
};

const DAY_LABEL: Record<string, string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
};

const dollars = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

/** Full booked date ("Monday, August 11, 2026"), weekday-only fallback. */
function formatClassDate(iso: string | null, dayChoice: string): string {
  if (iso) {
    const [y, m, d] = iso.split('-').map(Number);
    if (Number.isFinite(y)) {
      return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  }
  return DAY_LABEL[dayChoice] ?? dayChoice;
}

export function TrialSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [details, setDetails] = useState<TrialConfirmation | null>(null);
  const [detailsError, setDetailsError] = useState(false);

  useEffect(() => {
    track(events.trialSuccess, { sessionId });
    track(events.trialSignupConfirmation, {
      funnel: 'trial_signup',
      step: 'confirmation',
      sessionId,
    });
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    getTrialConfirmation(sessionId)
      .then((r) => {
        if (active) setDetails(r);
      })
      .catch(() => {
        if (active) setDetailsError(true);
      });
    return () => {
      active = false;
    };
  }, [sessionId]);

  const classLabel = details ? (CLASS_LABEL[details.classChoice] ?? details.classChoice) : null;
  const dayLabel = details ? formatClassDate(details.classDate, details.dayChoice) : null;
  const timeLabel =
    details?.slotStartTime && details.slotEndTime
      ? `${details.slotStartTime} - ${details.slotEndTime}`
      : details?.slotStartTime;

  return (
    <Section eyebrow="See you on the mats" title="Trial confirmed.">
      <div className="max-w-3xl space-y-6 text-bone/80">
        <p>Your trial is booked. Confirmation sent to your email.</p>

        {sessionId && !details && !detailsError && (
          <p className="text-sm text-bone/60">Loading your booking details...</p>
        )}

        {details && (
          <dl className="grid gap-3 rounded border border-bone/15 bg-neutral-900/50 p-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wider text-bone/50">Name</dt>
              <dd className="mt-1 font-semibold text-bone">{details.name}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-bone/50">Payment</dt>
              <dd className="mt-1 font-semibold text-bone">{dollars(details.amountCents)} paid</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-bone/50">Class</dt>
              <dd className="mt-1 font-semibold text-bone">
                {details.slotTitle ?? classLabel}
                {details.isMinor ? ' · minor participant' : ''}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-bone/50">When</dt>
              <dd className="mt-1 font-semibold text-bone">
                {dayLabel}
                {timeLabel ? ` · ${timeLabel}` : ''}
              </dd>
            </div>
          </dl>
        )}

        <section>
          <h2 className="font-display text-2xl text-bone">Location</h2>
          <p className="mt-2">
            <strong>612 Valley Ranch Pkwy S, Irving, TX 75063</strong>
          </p>
          <p className="mt-1 text-sm text-bone/65">
            Next to Discount Tire, behind CVS, 2 blocks north of I-635.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl text-bone">Bring to class</h2>
          <ul className="mt-2 list-disc pl-5 text-sm text-bone/75">
            <li>
              A good attitude &amp; comfortable clothes, no tank tops, no cutouts. Nothing else
              needed.
            </li>
          </ul>
        </section>

        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={
              details && CLASS_TO_PROGRAM[details.classChoice]
                ? `/schedule?programs=${CLASS_TO_PROGRAM[details.classChoice]}`
                : '/schedule'
            }
            className="btn-primary"
          >
            See schedule
          </Link>
          <a href="tel:+19729775605" className="btn-ghost">
            Call (972) 977-5605
          </a>
        </div>
      </div>
    </Section>
  );
}
